import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../assets/common/close.svg';

import { getPositionImage } from '../../../utils/helpers';

export const Modal = ({ openModal, setOpenModal }: { openModal: boolean; setOpenModal: (state: boolean) => void }) => {
    return (
        <Dialog
            onClose={() => {
                setOpenModal(false);
            }}
            open={openModal}
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '20px',
                    maxWidth: '804px',
                },
            }}
        >
            <Box>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpenModal(false)}
                    sx={{
                        position: 'absolute',
                        right: 10,
                        top: 10,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Typography component={'div'}>
                    <Typography
                        sx={{
                            color: '#0F0E0E',
                            fontFamily: 'AvenirNext700',
                            fontSize: '32px',
                            padding: '30px 50px 30px 30px',
                        }}
                    >
                        Clan Mastery Score: How It's Calculated
                    </Typography>
                    <Typography
                        component={'div'}
                        sx={{
                            color: '#9298A1',
                            fontFamily: 'AvenirNext400',
                            fontSize: '20px',
                            padding: '0 30px 30px',
                        }}
                    >
                        The Clan Mastery Scoreboard shows the full power of the clan, considering its performance across
                        three leaderboards: Clash Wins, Highest Win Rate, and WLKN gained. The score is calculated by
                        averaging the clan's positions across these leaderboards and then subtracting it from the total
                        number of currently competing clans. The higher the score, the higher is the position in the
                        Clan Mastery chart.
                        <br />
                        <br />
                        The formula is:
                        <br />
                        <span style={{ color: '#0F0E0E', fontFamily: 'AvenirNext500' }}>
                            score = NumOfClans - (((M1+M2+M3)/3) - Km1 - Km2 - Km3)
                        </span>
                        <br />
                        <br />
                        <i>where</i>
                        <br />
                        NumOfClans - number of currently registered clans;
                        <br />
                        <br />
                        M1, M2, M3 - clan's positions in 3 leaderboards;
                        <br />
                        Km1, Km2, Km3 - bonus coefficients for top places in 3 leaderboards:
                        <br />
                        1st place = 0.3
                        <br />
                        2nd place = 0.2
                        <br />
                        3rd place = 0.1
                        <br />
                        4th place and on = 0
                    </Typography>
                    <Typography
                        component={'div'}
                        sx={{
                            backgroundColor: '#F6F9FC',
                            paddingBottom: '30px',
                        }}
                    >
                        <Typography
                            component={'div'}
                            sx={{
                                color: '#0F0E0E',
                                fontFamily: 'AvenirNext600',
                                fontSize: '20px',
                                padding: '30px 30px 0px 30px',
                            }}
                        >
                            For example:
                        </Typography>

                        <Box
                            sx={{
                                padding: '15px 30px',
                                display: 'flex',
                            }}
                        >
                            <Box
                                sx={{
                                    height: '1px',
                                    minWidth: '20px',
                                    backgroundColor: '#D8DFE5',
                                    marginTop: '20px',
                                    marginRight: '5px',
                                }}
                            ></Box>
                            <Typography
                                component={'span'}
                                sx={{
                                    color: '#9298A1',
                                    fontFamily: 'AvenirNext400',
                                    fontSize: '16px',
                                }}
                            >
                                Out of 10 registered clans,{' '}
                                <span style={{ color: '#81C259', fontFamily: 'AvenirNext500' }}>Clan1</span> managed to
                                secure 1st place in Clash Wins chart, 1st place in WLKN Gain chart, and 4th place in Win
                                rate chart. Its Clan Mastery Score is:
                                <br />
                                10 - ((1+1+4)/3) - 0.3 - 0.3 - 0) ={' '}
                                <span style={{ color: '#0F0E0E', fontFamily: 'AvenirNext500' }}> 8.6</span>
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                padding: '15px 30px',
                                display: 'flex',
                            }}
                        >
                            <Box
                                sx={{
                                    height: '1px',
                                    minWidth: '20px',
                                    backgroundColor: '#D8DFE5',
                                    marginTop: '20px',
                                    marginRight: '5px',
                                }}
                            ></Box>
                            <Typography
                                component={'span'}
                                sx={{
                                    color: '#9298A1',
                                    fontFamily: 'AvenirNext400',
                                    fontSize: '16px',
                                }}
                            >
                                <span style={{ color: '#47ACF3', fontFamily: 'AvenirNext500' }}>Clan2</span> managed to
                                secure 1st place in Clash Wins chart, 3rd place in WLKN Gain chart, and 5th place in Win
                                rate chart. Its Clan Mastery Score is:
                                <br />
                                10 - ((1+3+5)/3) - 0.3 - 0.1 - 0) ={' '}
                                <span style={{ color: '#0F0E0E', fontFamily: 'AvenirNext500' }}>7.4</span>
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                padding: '15px 30px',
                                display: 'flex',
                            }}
                        >
                            <Box
                                sx={{
                                    height: '1px',
                                    minWidth: '20px',
                                    backgroundColor: '#D8DFE5',
                                    marginTop: '20px',
                                    marginRight: '5px',
                                }}
                            ></Box>
                            <Typography
                                component={'span'}
                                sx={{
                                    color: '#9298A1',
                                    fontFamily: 'AvenirNext400',
                                    fontSize: '16px',
                                }}
                            >
                                Let's consider one more clan,{' '}
                                <span style={{ color: '#826DD4', fontFamily: 'AvenirNext500' }}>Clan3</span>. It scored
                                6th place in Clash Wins chart, 1st place in WLKN Gain chart, and 8th place in Win rate
                                chart. Its Clan Mastery Score is:
                                <br />
                                10 - ((6+1+8)/3) - 0 - 0.3 - 0) ={' '}
                                <span style={{ color: '#0F0E0E', fontFamily: 'AvenirNext500' }}>5.3</span>
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                padding: '0px 30px',
                            }}
                        >
                            <Box
                                sx={{
                                    height: '1px',
                                    minWidth: '20px',
                                    backgroundColor: '#D8DFE5',
                                    marginBottom: '10px',
                                    marginRight: '5px',
                                    display: 'inline-block',
                                }}
                            ></Box>
                            <Typography
                                component={'span'}
                                sx={{
                                    color: '#9298A1',
                                    fontFamily: 'AvenirNext400',
                                    fontSize: '16px',
                                }}
                            >
                                Final tournament results:
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#9298A1',
                                    fontFamily: 'AvenirNext400',
                                    fontSize: '16px',
                                    margin: '15px',
                                    '& img': {
                                        width: '24px',
                                        margin: '-7px 7px -7px 10px',
                                    },
                                }}
                            >
                                {getPositionImage(1)}
                                <span style={{ color: '#81C259', fontFamily: 'AvenirNext500' }}>Clan1</span> (score 8.6)
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#9298A1',
                                    fontFamily: 'AvenirNext400',
                                    fontSize: '16px',
                                    margin: '15px',
                                    '& img': {
                                        width: '24px',
                                        margin: '-7px 7px -7px 10px',
                                    },
                                }}
                            >
                                {getPositionImage(2)}
                                <span style={{ color: '#47ACF3', fontFamily: 'AvenirNext500' }}>Clan2</span> (score 7.4)
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#9298A1',
                                    fontFamily: 'AvenirNext400',
                                    fontSize: '16px',
                                    margin: '15px',
                                    '& img': {
                                        width: '24px',
                                        margin: '-7px 7px -7px 10px',
                                    },
                                }}
                            >
                                {getPositionImage(3)}
                                <span style={{ color: '#826DD4', fontFamily: 'AvenirNext500' }}>Clan3</span> (score 5.3)
                            </Typography>
                        </Box>
                    </Typography>
                </Typography>
            </Box>
        </Dialog>
    );
};
